export default {
  corePages: {
    startGuide: {
      pageTitle: "Start guide",
      pageSubtitle: "Complete the setup tasks to get started",
      pageSubtitleProject: "Complete these tasks to set up {{project}}",
      showPreviousTasks: "Show previous tasks",
      tasksLeft: "{{tasksLeft}} tasks left (About {{minutesLeft}} minutes)",
      openHelpCenter: "Open Help Center",
      openMessages: "Open Messages",
      checkDocumentRequests: "Check document requests",
      fill: "Fill",
      learn: "Learn",
      learnMore: "Learn more",
      skipped: "Skipped",
      setupComplete: "Setup complete.",
      back: "Back",
      next: "Next",
      skip: "Skip",
      close: "Close",
      stepsAriaLabel: "Setup guide steps",
      nextActions: "Next actions",
      nextActionsDescription: "Based on what is already set up",
      backToGuide: "Back to setup guide",
      taskOf: "Task {{current}} of {{total}}",
      setupTask: "Setup task",
      completeAfterSave: "This task is marked complete after the page is saved.",
      openPrerequisite: "Open {{name}}",
      completePreviousStep: "Complete the previous required step first.",
      businessDetails: {
        name: "Business details",
        description:
          "Open Business settings, then Business. Enter the company details in English and without special characters. These details are used for filing declarations.",
      },
      paymentDetails: {
        name: "Payment details",
      },
      help: {
        name: "Help",
      },
      tax: {
        subtitle: "Here are some tips and setup tasks to help you get started",
        goal: "Use this guide to get ready for your first tax return.",
        allDone: "You've completed all setup tasks!",
        goToTransactions: "Go to Transactions",
        nextActions: {
          vatReturns: {
            title: "Review VAT returns",
            description: "Open a return once your numbers and transactions are in place.",
            label: "Go to VAT returns",
          },
          salesTax: {
            title: "Check sales tax returns",
            description: "Sales tax permits and returns live with your tax settings.",
            label: "Open tax settings",
          },
          store: {
            title: "Add another store",
            description: "Connect another marketplace or shop when you are ready.",
            label: "Go to stores",
          },
        },
        steps: {
          profile: {
            name: "Fill your profile",
            description:
              "Use the English language to fill in your address and company name. Information from your profile is used in VAT returns.",
          },
          store: {
            name: "Add a store",
            description:
              "Connect your Amazon, Shopify, marketplace, or custom store. You can configure a domestic country for every store.",
          },
          settings: {
            name: "Fill tax settings",
            description:
              "Add and activate your VAT numbers or sales tax permits before uploading transactions because they affect tax calculations.",
          },
          transactions: {
            name: "Upload transactions",
            description:
              "Upload transactions using a marketplace integration, spreadsheet, API, or plugin.",
            bullets: {
              marketplace: "Marketplace integration",
              excel: "Manual upload from an Excel spreadsheet",
              api: "Real-time API integration",
              plugins: "Magento or WooCommerce plugins",
            },
          },
        },
      },
      epr: {
        subtitle:
          "The top left corner should say L'EPR. If it does not, click the 9-dot icon in the top right and select L'EPR.",
        allDone: "You've completed the L'EPR setup tasks.",
        goal: "Use this guide to get ready for your first EPR declaration.",
        viewReportingCalendar: "View reporting calendar",
        nextActions: {
          declarations: {
            title: "Review EPR declarations",
            description: "Open a draft or start the next declaration from the reporting calendar.",
            label: "Go to declarations",
          },
          messages: {
            description: "Eco-operator requests appear in Messages.",
          },
          calendar: {
            title: "See upcoming deadlines",
            description: "The reporting calendar lists what is due and when.",
            label: "Open calendar",
          },
        },
        steps: {
          payments: {
            description:
              "In Payments, specify the company that pays for the subscription and receives invoices, and select a currency. UK paying companies are billed in pounds sterling.",
          },
          registration: {
            name: "EPR registration numbers",
            description:
              "Use the Registration tracker to follow registrations handled by Lappa. If you already have an EPR number, add it in EPR settings. Activation requests and required documents appear in Messages.",
            track: "Track a Lappa registration",
          },
          data: {
            name: "Add EPR data",
            description:
              "Add reporting data through an integration, an Excel upload, Packaging SKU, or directly in a declaration. Transactions is the best place to review imported and uploaded data.",
          },
          calendar: {
            name: "Reporting calendar",
            description:
              "When your EPR numbers are active, use the reporting calendar to see which declarations are due and when. You can start a declaration from a calendar deadline.",
            action: "Open reporting calendar",
          },
          declaration: {
            name: "File an EPR declaration",
            description:
              "Open EPR declarations and click + Fill EPR declaration. Select the country and reporting period, check any pre-filled data, complete the country-specific steps, and submit when ready.",
            lockReason:
              "This task opens after you add an active EPR registration number. Declarations are filed per country and number.",
          },
          help: {
            description:
              "Use Messages for requests from eco-operators. Open the Help Center for country guides and support, or use live chat during support hours.",
          },
        },
      },
      authorize: {
        subtitle:
          "The top left corner should say L'Authorize. If it does not, click the 9-dot icon in the top right and select L'Authorize.",
        allDone: "You've completed the L'Authorize setup tasks.",
        goal: "Use this guide to publish your first digital packaging label.",
        goToPackaging: "Go to Packaging",
        nextActions: {
          labels: {
            title: "Review packaging labels",
            description: "Print a saved label or copy its digital link.",
          },
          messages: {
            description: "Requests for an authorised representative appear in Messages.",
          },
        },
        steps: {
          payments: {
            description:
              "In Payments, specify the company that pays for the subscription and receives invoices, and select a currency. If that company is based in the UK, the currency choice is not available: payments are always in pounds sterling.",
          },
          packaging: {
            name: "Add packaging",
            description:
              "Go to Packaging and click + Add packaging. You can skip steps when you do not have the information. If the packaging is single-use and not made from recycled materials, skip eco-modulation: that data does not change the eco-fee.",
          },
          label: {
            name: "Digital label (PPWR)",
            description:
              "Create a label only after the packaging is saved and marked Active. In Packaging, click the three dots next to the item, select Edit, and open the Digital label (PPWR) tab. Fill in every step. You can then print the label or copy its digital link.",
            lockReason: "Save the packaging and mark it Active before creating a digital label.",
          },
          declaration: {
            name: "Declaration of conformity",
            description:
              "Optional. If the declaration should open from the QR code together with the label, upload a PDF of your existing Declaration of Conformity on the Files step of the Digital label (PPWR) form.",
            lockReason:
              "Create the digital label first. The declaration PDF is uploaded on its Files step.",
          },
          representative: {
            name: "Authorised representative",
            description:
              "Optional. If you appoint Lappa as your Authorised Representative, a document request appears in Messages shortly after you complete the label form. Upload the files there. After the documents are verified, the label goes live online.",
            lockReason:
              "Complete the label form first. The document request then appears in Messages.",
          },
          help: {
            description:
              "While filling in a form, use the live chat in the bottom-right corner. Specialists are available Monday to Friday, 9:00–17:00 UK time. Outside those hours, open a case in the Help Center. On some plans the Help Center also shows your account manager.",
          },
        },
      },
      invoice: {
        steps: {
          vatNumber: "Add your VAT number",
          peppolNumber: "Add your PEPPOL number",
          bankInformation: "Add bank information",
        },
      },
    },
    taxSettings: {
      editActiveStatus: "Edit active status",
      editActiveStatusFor: "Do you want to edit active status for",
      activateVatQuestion: "Do you want to activate VAT number {{number}} for {{reason}} in {{country}}?",
      deactivateVatQuestion: "Do you want to deactivate VAT number {{number}} for {{reason}} in {{country}}?",
      activate: "Activate",
      deactivate: "Deactivate",
      ok: "OK",
      vatRep: "VAT rep",
      vatNoData: "Add VAT registrations to see data",
      addVatRegistration: "Add VAT registration",
      addVatCertificate: "Add new VAT certificate",
      enterTinNumber: "Please enter TIN number",
      appointLappaRep: "I'd like to appoint Lappa as my tax representative",
      merchantsSettings: "Merchants settings",
      periodicityAnnual: "Annual",
      periodicityQuarterly: "Quarterly",
      periodicityMonthly: "Monthly",
      periodicityBiAnnual: "Bi-Annual",
      periodicityYear: "Year",
      periodicityQuarter: "Quarter",
      periodicityMonth: "Month",
      salesTaxReport: "Sales Tax Report",
      salesTaxPrepaymentReport: "Sales Tax Prepayment Report",
      prepaymentType: "Prepayment type",
      prepaymentTypeCurrentPeriod: "Current-period % of liability (e.g., 90% of June)",
      prepaymentTypeMonthToDate: "Month-to-date (e.g., 1st–15th of month)",
      prepaymentTypePriorPeriod: "Prior-period proxy (e.g., % of May)",
      prepaymentTypePriorYear: "Prior-year same period (e.g., 1/3 of last Q2)",
      activateSalesTaxPermitQuestion: "Do you want to activate Sales Tax Permit {{number}} in {{state}}?",
      deactivateSalesTaxPermitQuestion: "Do you want to deactivate Sales Tax Permit {{number}} in {{state}}?",
      activateWaiverQuestion: "Do you want to activate Waiver {{number}} in {{state}}?",
      deactivateWaiverQuestion: "Do you want to deactivate Waiver {{number}} in {{state}}?",
      activateExemptionQuestion: "Do you want to activate Exemption {{number}} in {{state}}?",
      deactivateExemptionQuestion: "Do you want to deactivate Exemption {{number}} in {{state}}?",
      activateItemQuestion: "Do you want to activate this item in {{state}}?",
      deactivateItemQuestion: "Do you want to deactivate this item in {{state}}?",
      editSalesTaxPermitIn: "Edit Sales tax permit {{number}} in {{state}}",
      editExemptionIn: "Edit Exemption {{number}} in {{state}}",
      editWaiverIn: "Edit Waiver {{number}} in {{state}}",
      deletePermitQuestion: "Do you want to delete {{type}}",
      fromDateQuestion: "from date {{date}}?",
      deleteExemptionQuestion: "Do you want to delete tax exemption certificate",
      deleteWaiverQuestion: "Do you want to delete waiver",
      download: "Download",
      errSelectState: "Please select state",
      errEnterSalesTaxId: "Please enter sales tax id",
      errSelectCertType: "Please select certificate type",
      errSelectPermitPeriodicity: "Please select permit periodicity",
      errSelectExemptionCert: "Please select tax-exemption certificate",
      errSelectCertNumber: "Please select certificate number",
      errEnterExemptionCert: "Please enter tax-exemption certificate",
      errEnterCertNumber: "Please enter certificate number",
      errEnterRegNumber: "Please enter registration number",
      companyName: "Company name",
      errEnterName: "Please enter name",
      authId: "Auth ID",
      errEnterAuthId: "Please enter authorised representative's ID",
      addSystemOperator: "Add System operator",
      eprCategory: "EPR category",
      eprRepresentative: "EPR representative",
      eprNoData: "Add EPR to see data",
      deleteEprQuestion: "Do you want to delete EPR {{number}} in {{country}}?",
      activateEprQuestion: "Do you want to activate EPR {{number}} in {{country}}?",
      deactivateEprQuestion: "Do you want to deactivate EPR {{number}} in {{country}}?",
      selectCountry: "Select country",
      selectDate: "Select date",
      deleteMerchantQuestion: "Do you want to delete merchant id {{id}}?",
      merchantId: "Merchant id",
      countryOfEstablishment: "Country of establishment",
      merchantName: "Merchant name",
      merchantEuVat: "Merchant EU VAT number",
      merchantOss: "Merchant OSS number",
      merchantUkVat: "Merchant UK VAT number",
      merchantDepartureCountry: "Merchant default Departure Country",
      addNewMerchant: "Add new merchant",
      merchantsNoData: "Add merchants to see data",
      availableMerchants: "Number of available merchants:",
      success: "Success",
      fileName: "File: \"{{name}}\"",
      uploadMerchants: "Upload merchants",
      uploadMerchantsTooltip: "You can upload a dataset for Merchant file here",
      chooseFilePrefix: "choose",
      chooseFileSuffix: "file to upload",
      downloadTemplateLower: "download template",
      autopilotSelectCountry: "Please select at least one country from the list.",
      autopilotSelectStore: "Please select all stores from the list.",
      activateAutopilotQuestion: "Do you want to activate Autopilot?",
      autopilotAgreement: "I agree to turn on this feature for free for the first month. Then it will be charged 10% of monthly payment amount.",
      turnOn: "Turn on",
      inventory: {
        description: "Description",
        commodityCode: "Commodity code",
        countryOfOrigin: "Country of Origin",
        vatRate: "VAT rate",
        purchasePrice: "Purchase price",
        currency: "Currency",
        importValidationErrors: "Import validation errors:",
        line: "Line {{line}}:",
        searchPlaceholder: "Search by SKU or description...",
        importInventory: "Import inventory",
        import: "Import",
        importDescription: "Import inventory transaction CSV/Excel file.",
        downloadTemplate: "Download template",
        recalculate: "Recalculate",
        recalculateInventory: "Recalculate inventory",
        recalculated: "Inventory recalculated successfully",
        dateFrom: "Date from",
        dateTo: "Date to",
        standard: "Standard",
        nonStandard: "Non-standard",
        editItem: "Edit inventory item",
        enterSku: "Enter SKU",
        enterCommodityCode: "Enter commodity code",
        enterDescription: "Enter description",
        enterPurchasePrice: "Enter purchase price",
        selectCurrency: "Select currency",
        selectCountryOfOrigin: "Select country of origin",
        selectFileFirst: "Please select a file first",
        validationErrors: "Validation errors in uploaded data",
        fileUploaded: "File uploaded successfully",
        uploadError: "An error occurred during file upload",
      },
    },
    subscription: {
      additionalServices: "Additional services",
      enterInvoiceInfo: "Please enter invoice information",
      addMore: "Add more",
      addService: "Add service",
      addToOrder: "Add to order",
      buyAddon: "Buy add-on",
      noAddonsForPlan: "No add-ons available for this plan",
      changeAddonsAmount: "Change amount of add-ons",
      oneTimeAddons: "One-time add-ons",
      removeAddon: "Remove add-on",
      chooseCategory: "Choose category",
      chooseType: "Choose type",
      year: "Year",
      states: "States",
      selectRange: "Select range",
      selectService: "Select service",
      unitAmount: "Unit amount",
      withoutVat: "Without VAT",
      withVat: "With VAT",
      includeVat: "Include VAT in amount",
      noServices: "No services",
      periodRange: "from {{from}} till {{till}}",
      item: "Item",
      planName: "{{name}} plan",
      orderSummary: "Order summary",
      tariffPlanLabel: "Tariff plan:",
      countriesLabel: "Countries:",
      bankDescription: "Bank description",
      paymentError: "Payment error: {{message}}",
      stripeNotInitialized: "Stripe is not initialized. Please wait a moment and try again.",
      stripeInitFailed: "Stripe initialization failed. Please try again.",
      paymentRedirectFailed: "Payment redirect failed. Please try again.",
      invalidServerResponse: "Invalid response from server",
      cardRedirectFailed: "Credit card redirect failed",
      shopifyRedirectFailed: "Shopify Pay redirect failed",
      orderCreditsFailed: "Failed to order credits",
      promoAppliedNoDiscount: "Promo code applied successfully",
      replaceComma: "Please replace comma with a dot",
      creditsOrderSuccess: "Your credits order has been successfully placed! We will process your payment and add the credits to your account shortly.",
      compareFeatures: "Compare features by plan",
      compareFeaturesSubtitle: "Easily compare features across all available plans",
      features: "Features",
      unlimited: "Unlimited",
      infoIcon: "Info",
      consultationsTooltip: "If you have a specific question or situation and would like our advice. Our experts provide personalized recommendations to address your tax-related challenges",
      featureNames: {
        countries: "Countries",
        states: "States",
        transactionsPerMonth: "Number of transactions per month",
        consultationsSupport: "Consultations support",
        integrations: "Integrations",
        intrastat: "Intrastat",
        esSalesList: "ES list of sales",
        thresholdCalculation: "Threshold calculation",
        totalTransactionsValue: "Total value of transactions per month",
        distantSalesReturns: "Distant sales returns",
        digitalServicesTaxReturns: "Digital services tax returns",
        salesTaxCalculation: "Sales tax calculation",
        weight: "Weight",
        subscriptionOnePack: "Subscription (1 pack credit)",
        numberOfUsers: "Number of users",
        packaging: "Packaging",
        oneReportAutoSubmit: "One report auto-submit",
        reminders: "Reminders",
        receive: "Receive",
        receivingEinvoices: "Receiving e-invoices per year",
        sendingEinvoices: "Sending e-invoices per year",
        sendEinvoices: "Send e-invoices per year",
        receiveEinvoices: "Receive e-invoices per year",
        riskLevel: "Risk level",
        digitalLabels: "Digital labels",
        apiAccess: "API access",
        complianceFileReview: "Compliance file review",
        productTraceability: "Product traceability verification",
        regulatorCommunication: "Regulator communication handling (forward + respond)",
        incidentResponse: "Incident response management",
        namedComplianceManager: "Named compliance manager",
        crisisPlaybooks: "Crisis handling playbooks",
        dedicatedArContact: "Dedicated AR contact",
        auditSupport: "Audit support",
        customReporting: "Custom reporting",
        multiMarketCoordination: "Multi-market coordination",
        numberOfMerchants: "Number of merchants",
        numberOfTransactions: "Number of transactions",
        usSalesTaxCalculation: "US Sales tax calculation",
        euVatCalculation: "EU VAT calculation",
        exportImportCalculation: "Export, import calculation",
        merchantsTaxReports: "Merchants tax reports",
        multiCurrency: "Multi currency",
      },
    },
    addons: {
      tabs: {
        vat: "VAT add-ons",
        salesTax: "Sales tax add-ons",
        omp: "OMP add-ons",
        epr: "EPR add-ons",
        invoice: "Invoice add-ons",
        authorize: "Authorize add-ons",
      },
      noSubscriptions: "No subscriptions available",
    },
  },
};
